import { Component, inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { CommonModule } from '@angular/common';
import { Project } from 'src/app/entities/project/project';
import { ProjectRepository } from 'src/app/services/remote-data-service/repositories/project-repository/project.repository';
import { ClientRepository } from 'src/app/services/remote-data-service/repositories/person-repository/client.repository';
import { UserRepository } from 'src/app/services/remote-data-service/repositories/user-repository/user.repository';
import { TagRepository } from 'src/app/services/remote-data-service/repositories/tag-repository/tag.repository';
import { TimeTrackRepository } from 'src/app/services/remote-data-service/repositories/time-track-repository/time-track.repository';
import { catchError, map, Observable } from 'rxjs';
import { SnackbarService } from 'src/app/services/snackbar-service/snackbar.service';
import { StoreService } from 'src/app/services/store-service/store.service';
import { TimeTrack } from 'src/app/entities/time-track/time-track';
import { Client } from 'src/app/entities/client/client';
import { User } from 'src/app/entities/user/user';
import { Tag } from 'src/app/entities/tag/tag';


/**
 * Generic interface for entities that can be deleted.
 * Ensures that all handled entities have a unique identifier.
 */
interface EntityBase {

  // This ensures that all handled entities have a unique identifier
  id: string;

  // Optional properties for display purposes
  name?: string;
  label?: string;
  title?: string;
}


/**
 * A reusable component for handling bulk deletion of various entity types.
 * Supports Projects, Clients, Users, Tags, and TimeTracks.
 * 
 * @template T - The entity type extending EntityBase that this component will handle
 */
@Component({
  selector: 'app-entity-items-annihilator',
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatCheckboxModule,
    MatDialogModule
  ],
  templateUrl: './entity-items-annihilator.component.html',
  styleUrl: './entity-items-annihilator.component.scss'
})
export class EntityItemsAnnihilatorComponent<T extends EntityBase> {
  // Dialog references and input data
  dialogRef = inject(MatDialogRef<EntityItemsAnnihilatorComponent<T>>);
  /** The entities passed to the dialog for potential deletion */
  data = inject(MAT_DIALOG_DATA).entities as T[];
  /** Type projection for template type checking */
  typeProyection?: T;

  // Dialog text configuration
  title = 'Delete Items';
  submitButton = 'Delete Selected';

  /** Stores IDs of selected items for deletion */
  selectedItems: string[] = [];

  // Local copies of entity arrays updated from store observables
  timeTracks: TimeTrack[] = [];
  projects: Project[] = [];
  tags: Tag[] = [];
  clients: Client[] = [];
  users: User[] = [];

  // Observable streams from the store service
  timeTracks$: Observable<TimeTrack[]>;
  projects$: Observable<Project[]>;
  tags$: Observable<Tag[]>;
  clients$: Observable<Client[]>;
  users$: Observable<User[]>;


  /**
   * Initializes component dependencies and entity streams
   * @param store - Service for managing application state
   * @param timeTracksRep - Repository for time track operations
   * @param projectRep - Repository for project operations
   * @param tagsRep - Repository for tag operations
   * @param clientsRep - Repository for client operations
   * @param userRep - Repository for user operations
   * @param snackBar - Service for displaying notifications
   */
  constructor(
    private store: StoreService,
    private timeTracksRep: TimeTrackRepository,
    private projectRep: ProjectRepository,
    private tagsRep: TagRepository,
    private clientsRep: ClientRepository,
    private userRep: UserRepository,
    private snackBar: SnackbarService
  ) {
    this.timeTracks$ = this.store.timeTracks;
    this.projects$ = this.store.projects;
    this.tags$ = this.store.tags;
    this.clients$ = this.store.clients;
    this.users$ = this.store.users;

    this.timeTracks$.subscribe((timeTracksRes) => {
      this.timeTracks = timeTracksRes;
    })

    this.projects$.subscribe((projectsRes) => {
      this.projects = projectsRes;
    })

    this.tags$.subscribe((tagsRes) => {
      this.tags = tagsRes
    })

    this.clients$.subscribe((clientsRes) => {
      this.clients = clientsRes;
    })

    this.users$.subscribe((usersRes) => {
      this.users = usersRes;
    })
  }


  /**
   * Toggles an item's selection state
   * @param id - The ID of the entity to toggle
   */
  toggleSelection(id: string) {
    if (this.selectedItems.includes(id)) {
      this.selectedItems = this.selectedItems.filter(itemId => itemId !== id);
    } else {
      this.selectedItems.push(id);
    }
  }

  /**
   * Performs bulk deletion of selected entities based on their type.
   * Handles error reporting and updates local state/store after successful deletions.
   * 
   * Process:
   * 1. Determine entity type from first item in data array
   * 2. Iterate through selected item IDs
   * 3. For each entity:
   *    - Find local instance
   *    - Call repository delete method
   *    - Handle errors with snackbar notifications
   *    - Update local state and store on success
   */
  deleteSelected() {
    // Reference to input data for closure access in nested callbacks
    const data = this.data;

    // Determine entity type and handle each case
    switch (true) {
      case data[0] instanceof Project: {
        // Handle project deletions
        for (const projectId of this.selectedItems) {
          // Find project in local state
          const project = this.projects.find(project => project.id === projectId);

          if (!project) {
            console.warn(`Project ${projectId} not found in local store`);
            return;
          }

          // Execute delete operation through repository
          this.projectRep.removeProject(project).pipe(
            // Handle any errors during deletion
            catchError(() => {
              return []
            }),
            // Update local state after successful deletion
            map(() => {
              // Remove from projects array
              this.projects = this.projects.filter(e => e.id !== project.id);
              // Remove from input data array
              this.data = this.data.filter(e => e.id !== project.id);
            })
          ).subscribe(() => {
            // Persist updated state to global store
            this.store.saveProjects(this.projects);
          })
        };

        break
      }
      case data[0] instanceof Client: {
        for (const clientId of this.selectedItems) {
          const client = this.clients.find(client => client.id === clientId);

          if (!client) {
            return
          }

          this.clientsRep.removeClient(client).pipe(
            catchError(() => {
              return []
            }),
            map(() => {
              this.clients = this.clients.filter(e => e.id !== client.id);
              this.data = this.data.filter(e => e.id !== client.id);
            })
          ).subscribe(() => {
            this.store.saveClients(this.clients);
          })
        };

        break
      }
      case data[0] instanceof User: {
        for (const userId of this.selectedItems) {
          const user = this.users.find(user => user.id === userId);

          if (!user) {
            return
          }

          this.userRep.deleteUser(user).pipe(
            catchError(() => {
              return []
            }),
            map(() => {
              this.users = this.users.filter(e => e.id !== user.id);
              this.data = this.data.filter(e => e.id !== user.id);
            })
          ).subscribe(() => {
            this.store.saveUsers(this.users);
          })
        };

        break
      }
      case data[0] instanceof Tag: {
        for (const tagId of this.selectedItems) {
          const tag = this.tags.find(tag => tag.id === tagId);

          if (!tag) {
            return
          }

          this.tagsRep.removeTag(tag).pipe(
            catchError(() => {
              return []
            }),
            map(() => {
              this.tags = this.tags.filter(e => e.id !== tag.id);
              this.data = this.data.filter(e => e.id !== tag.id);
            })
          ).subscribe(() => {
            this.store.saveTags(this.tags);
          })
        };

        break
      }
      case data[0] instanceof TimeTrack: {
        for (const timeTrackId of this.selectedItems) {
          const timeTrack = this.timeTracks.find(timeTrack => timeTrack.id === timeTrackId);

          if (!timeTrack) {
            return
          }

          this.timeTracksRep.removeTrack(timeTrack).pipe(
            catchError(() => {
              return []
            }),
            map(() => {
              this.timeTracks = this.timeTracks.filter(e => e.id !== timeTrack.id);
              this.data = this.data.filter(e => e.id !== timeTrack.id);
            })
          ).subscribe(() => {
            this.store.saveTimeTracks(this.timeTracks);
          })
        };

        break
      }
      default:
        this.snackBar.showError('No entity type matched')
        break;
    }

    this.dialogRef.close(this.selectedItems);
  }
}
